import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ExternalLink } from "lucide-react";
import api from "@/lib/api";
import { InterviewCard, LoadingBlock } from "@/components/site/Cards";

export default function OperatorDetail() {
  const { slug } = useParams();
  const [op, setOp] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/operators/${slug}`).then((r) => setOp(r.data)).finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <LoadingBlock />;
  if (!op) return <div className="p-20 text-center">Operator not found.</div>;

  return (
    <div data-testid="operator-detail">
      <section className="border-b border-oc-hairline bg-oc-sand">
        <div className="max-w-editorial mx-auto px-6 md:px-10 py-16 md:py-24">
          <Link to="/operators" className="inline-flex items-center gap-2 font-ui text-xs font-bold uppercase tracking-widest text-oc-ink hover:text-oc-blue mb-8">
            <ArrowLeft className="w-3.5 h-3.5" /> All Operators
          </Link>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-start">
            {op.portrait && (
              <div className="md:col-span-4">
                <div className="aspect-square overflow-hidden bg-oc-sand-warm">
                  <img src={op.portrait} alt={op.name} className="w-full h-full object-cover" />
                </div>
              </div>
            )}
            <div className="md:col-span-8">
              <div className="eyebrow text-oc-coral mb-4">Featured Operator{op.industry_slug ? ` · ${op.industry_slug.replace(/-/g, " ")}` : ""}</div>
              <h1 className="font-editorial text-4xl md:text-6xl leading-[1.03] tracking-tight text-oc-ink">{op.name}</h1>
              <p className="mt-4 font-editorial text-xl md:text-2xl text-oc-ink-soft leading-snug">
                {op.role}{op.company ? ` · ${op.company}` : ""}
              </p>
              {op.location && <div className="mt-2 font-ui text-sm text-oc-ink-muted">{op.location}</div>}
              {op.short_bio && (
                <p className="mt-8 font-editorial text-lg text-oc-ink leading-relaxed max-w-2xl">
                  {op.short_bio}
                </p>
              )}
              <div className="mt-6 flex flex-wrap gap-4 font-ui text-sm">
                {op.website && (
                  <a href={op.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-oc-blue hover:text-oc-coral">
                    Website <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                )}
                {op.linkedin && (
                  <a href={op.linkedin} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-oc-blue hover:text-oc-coral">
                    LinkedIn <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-editorial mx-auto px-6 md:px-10 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
          <div className="lg:col-span-8">
            {op.biography && (
              <div>
                <div className="eyebrow text-oc-coral mb-4">Biography</div>
                <p className="font-editorial text-xl leading-relaxed text-oc-ink">{op.biography}</p>
              </div>
            )}

            {op.principles?.length > 0 && (
              <div className="mt-16">
                <div className="eyebrow text-oc-coral mb-6">Operating Principles</div>
                <ul className="space-y-6">
                  {op.principles.map((p, i) => (
                    <li key={i} className="flex gap-6 pb-6 border-b border-oc-hairline last:border-0">
                      <span className="font-ui text-oc-coral font-bold text-sm mt-1 w-8 flex-shrink-0">{String(i + 1).padStart(2, "0")}</span>
                      <p className="font-editorial text-xl leading-snug text-oc-ink">"{p}"</p>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <aside className="lg:col-span-4 space-y-8">
            {op.areas?.length > 0 && (
              <div>
                <div className="eyebrow text-oc-ink-muted mb-3">Areas</div>
                <div className="flex flex-wrap gap-2">
                  {op.areas.map((a) => (
                    <span key={a} className="oc-chip">{a}</span>
                  ))}
                </div>
              </div>
            )}
            {op.topics?.length > 0 && (
              <div>
                <div className="eyebrow text-oc-ink-muted mb-3">Topics discussed</div>
                <div className="flex flex-wrap gap-2">
                  {op.topics.map((t) => (
                    <Link key={t} to={`/topics/${t}`} className="oc-chip">{t.replace(/-/g, " ")}</Link>
                  ))}
                </div>
              </div>
            )}
            {op.years_operating > 0 && (
              <div>
                <div className="eyebrow text-oc-ink-muted mb-2">Years operating</div>
                <div className="font-editorial text-3xl text-oc-ink">{op.years_operating}</div>
              </div>
            )}
          </aside>
        </div>
      </section>

      {op.interviews?.length > 0 && (
        <section className="bg-oc-sand border-t border-oc-hairline">
          <div className="max-w-editorial mx-auto px-6 md:px-10 py-20">
            <div className="eyebrow text-oc-coral mb-4">Featured Conversations</div>
            <h3 className="font-editorial text-3xl md:text-4xl mb-12">Interviews with {op.name.split(" ")[0]}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {op.interviews.map((iv) => (
                <InterviewCard key={iv.slug} interview={iv} />
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
